/**
 * 최초 로그인(임시 비밀번호) 강제 변경 · 내 계정 비밀번호 변경 — Figma 규칙 평가.
 * 공통: 10자 이상 + 문자 종류 3종 이상.
 */

export type ForceChangePasswordRules = {
  /** 10자 이상 */
  minLength: boolean;
  /** 영문 대/소문자·숫자·특수문자 중 3종 이상 */
  complexity: boolean;
  /** 임시 비밀번호와 다름 */
  differentFromTemp: boolean;
};

/** 내 계정「비밀번호 변경」모달 규칙 */
export type ChangePasswordRules = {
  minLength: boolean;
  complexity: boolean;
  /** 직전 3개(또는 현재) 비밀번호와 다름 */
  differentFromRecent: boolean;
};

const hasUpper = (value: string) => /[A-Z]/.test(value);
const hasLower = (value: string) => /[a-z]/.test(value);
const hasDigit = (value: string) => /\d/.test(value);
const hasSpecial = (value: string) => /[^A-Za-z0-9]/.test(value);

/** 문자 종류(대/소/숫자/특수) 충족 개수 */
export const countPasswordCharTypes = (password: string): number => {
  let count = 0;
  if (hasUpper(password)) count += 1;
  if (hasLower(password)) count += 1;
  if (hasDigit(password)) count += 1;
  if (hasSpecial(password)) count += 1;
  return count;
};

/**
 * @param temporaryPassword API 연동 전이면 null/undefined.
 *   비교값이 없을 때는 비어 있지 않은 비밀번호를 «다름»으로 통과시킨다(프리뷰용).
 */
export const evaluateForceChangePasswordRules = (
  password: string,
  temporaryPassword?: string | null,
): ForceChangePasswordRules => {
  const minLength = password.length >= 10;
  const complexity = countPasswordCharTypes(password) >= 3;

  let differentFromTemp = false;
  if (password.length === 0) {
    differentFromTemp = false;
  } else if (
    temporaryPassword != null &&
    String(temporaryPassword).length > 0
  ) {
    differentFromTemp = password !== temporaryPassword;
  } else {
    // 구분 파라미터 부재 시: 입력만 있으면 UI에서 통과 가능
    differentFromTemp = true;
  }

  return { minLength, complexity, differentFromTemp };
};

export const areForceChangePasswordRulesMet = (
  rules: ForceChangePasswordRules,
): boolean =>
  rules.minLength && rules.complexity && rules.differentFromTemp;

/**
 * 내 계정 비밀번호 변경 규칙.
 * recentPasswords가 없으면 현재 비밀번호와만 비교(API 연동 전).
 */
export const evaluateChangePasswordRules = (
  newPassword: string,
  options?: {
    currentPassword?: string;
    recentPasswords?: string[] | null;
  },
): ChangePasswordRules => {
  const minLength = newPassword.length >= 10;
  const complexity = countPasswordCharTypes(newPassword) >= 3;

  let differentFromRecent = false;
  if (newPassword.length === 0) {
    differentFromRecent = false;
  } else if (options?.recentPasswords?.length) {
    differentFromRecent = !options.recentPasswords.includes(newPassword);
  } else if (options?.currentPassword) {
    differentFromRecent = newPassword !== options.currentPassword;
  } else {
    // 이력·현재 비교값 부재: 입력만 있으면 통과(프리뷰)
    differentFromRecent = true;
  }

  return { minLength, complexity, differentFromRecent };
};

export const areChangePasswordRulesMet = (
  rules: ChangePasswordRules,
): boolean =>
  rules.minLength && rules.complexity && rules.differentFromRecent;
